import axios from "axios";
import { get } from "lodash";
import { IOption } from "../models/option.model";
import { FULFILLED, PENDING, REJECTED } from "./action-type.util";

const remapItem = item => ({ id: item["documentID"], ...item });
const remapItems = items => items.map(remapItem);

export const ACTION_TYPES = {
  FETCH_OPTIONS: "brokerContracts/FETCH_OPTIONS",
  FETCH_DETAILS: "brokerContracts/FETCH_DETAILS",
  FETCH_CONTRACTS: "brokerContracts/FETCH_CONTRACTS",
  FETCH_DOCUMENTS: "brokerContracts/FETCH_DOCUMENTS",
  DOWNLOAD_DOCUMENT: "brokerContracts/DOWNLOAD_DOCUMENT",
  UPLOAD_FILE: "brokerContracts/UPLOAD_FILE",
  TOGGLE_UPLOAD_TOAST: "brokerContracts/TOGGLE_UPLOAD_TOAST",
  DOWNLOAD_FILE: "brokerContracts/DOWNLOAD_FILE",
  DELETE_FILE: "brokerContracts/DELETE_FILE",
  DELETE_CONTRACT_DOCUMENTS: "brokerContracts/DELETE_CONTRACT_DOCUMENTS",
};

const initialState = {
  contracts: [],
  contractDocuments: [],
  contractDetails: null,
  inProcess: false,
  uploadModal: {
    documentTypes: new Array<IOption>(),
  },
  isUploadToastVisible: false,
  options: {
    selectStatusTypeList: new Array<IOption>(),
    selectCarrierList: new Array<IOption>(),
  },
};

export interface ContractingModalData {
  id?: number;
  file?: File;
  fileName?: string;
  carrierTypeID?: number;
  statusTypeID?: number;
  licenseNumber?: string;
  expirationDate?: string;
  notes?: string;
}

export type BrokerContractsState = Readonly<typeof initialState>;

/**
 * Correspondence Reducer
 */
export default (state: BrokerContractsState = initialState, action): BrokerContractsState => {
  switch (action.type) {
    // Pending
    case PENDING(ACTION_TYPES.FETCH_CONTRACTS):
    case PENDING(ACTION_TYPES.DOWNLOAD_DOCUMENT):
    case PENDING(ACTION_TYPES.UPLOAD_FILE):
    case PENDING(ACTION_TYPES.FETCH_DOCUMENTS):
    case PENDING(ACTION_TYPES.FETCH_DETAILS):
    case PENDING(ACTION_TYPES.DELETE_CONTRACT_DOCUMENTS): {
      return {
        ...state,
        inProcess: true,
      };
    }

    // Fulfilled
    case FULFILLED(ACTION_TYPES.DOWNLOAD_DOCUMENT): {
      return {
        ...state,
        inProcess: false,
      };
    }

    case FULFILLED(ACTION_TYPES.FETCH_CONTRACTS): {
      return {
        ...state,
        contracts: action.payload.data,
        inProcess: false,
      };
    }

    case FULFILLED(ACTION_TYPES.FETCH_DOCUMENTS): {
      return {
        ...state,
        contractDocuments: action.payload.data,
        inProcess: false,
      };
    }

    case FULFILLED(ACTION_TYPES.FETCH_OPTIONS): {
      return {
        ...state,
        options: action.payload.data,
      };
    }

    case FULFILLED(ACTION_TYPES.FETCH_DETAILS): {
      return {
        ...state,
        contractDetails: action.payload.data,
        inProcess: false,
      };
    }

    case FULFILLED(ACTION_TYPES.DELETE_CONTRACT_DOCUMENTS): {
      const deletedIds = action.meta?.deletedIds || [];
      return {
        ...state,
        contractDocuments: state.contractDocuments.filter(doc => !deletedIds.includes(doc.documentID)),
        inProcess: false,
      };
    }

    case FULFILLED(ACTION_TYPES.UPLOAD_FILE): {
      return {
        ...state,
        inProcess: false,
        isUploadToastVisible: true,
      };
    }

    // Rejected
    case REJECTED(ACTION_TYPES.DOWNLOAD_DOCUMENT):
    case REJECTED(ACTION_TYPES.FETCH_DOCUMENTS):
    case REJECTED(ACTION_TYPES.FETCH_DETAILS):
    case REJECTED(ACTION_TYPES.FETCH_CONTRACTS): {
      return {
        ...state,
        inProcess: false,
      };
    }

    case ACTION_TYPES.TOGGLE_UPLOAD_TOAST: {
      const isUploadToastVisible = get(action, "meta.open");
      return {
        ...state,
        isUploadToastVisible,
      };
    }

    default: {
      return {
        ...state,
      };
    }
  }
};

export const fetchBrokerContractsOptions = (brokerID: string) => {
  return {
    type: ACTION_TYPES.FETCH_OPTIONS,
    payload: axios.get(`brokers/${brokerID}/contracts/new`).then(response => {
      return response;
    }),
  };
};

export const fetchBrokerContractDetails = (brokerID: string, contractID: number) => ({
  type: ACTION_TYPES.FETCH_DETAILS,
  payload: axios.get(`brokers/${brokerID}/contracts/${contractID}/details`),
});

export const fetchBrokerContracts = (brokerID: string) => {
  return {
    type: ACTION_TYPES.FETCH_CONTRACTS,
    payload: axios.get(`brokers/${brokerID}/contracts`).then(response => {
      return response;
    }),
  };
};

export const fetchContractDocuments = (brokerID: string, contractID) => {
  return {
    type: ACTION_TYPES.FETCH_DOCUMENTS,
    payload: axios.get(`brokers/${brokerID}/contracts/${contractID}/documents`).then(response => {
      return response;
    }),
  };
};

export const uploadBrokerContract = (brokerID: string, data: ContractingModalData) => {
  const formData = new FormData();
  if (data.file) {
    formData.append("Files", data.file);
  } else {
    formData.append("Dto.KeepSameFile", "true");
  }
  formData.append("Dto.CarrierTypeID", data.carrierTypeID?.toString());
  formData.append("Dto.StatusTypeID", data.statusTypeID?.toString());
  formData.append("Dto.LicenseNumber", data.licenseNumber || "");
  formData.append("Dto.ExpirationDate", data.expirationDate);
  formData.append("Dto.Notes", data.notes || "");
  formData.append("Dto.DocumentId", data.id ? data.id.toString() : "0");

  return {
    type: ACTION_TYPES.UPLOAD_FILE,
    payload: axios.post(`brokers/${brokerID}/contracts`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  };
};

export const downloadBrokerContract = (brokerID: string, documentID: string) => {
  return {
    type: ACTION_TYPES.DOWNLOAD_FILE,
    payload: axios.get(`brokers/${brokerID}/contracts/${documentID}`, { responseType: "blob" }),
  };
};

export const deleteBrokerContract = (brokerID: string, contractID: string) => {
  return {
    type: ACTION_TYPES.DELETE_FILE,
    payload: axios.delete(`brokers/${brokerID}/contracts/${contractID}`),
  };
};

export const deleteContractDocuments = (brokerID: string, contractID: number, documentIDs: number[]) => {
  return {
    type: ACTION_TYPES.DELETE_CONTRACT_DOCUMENTS,
    payload: axios.delete(`brokers/${brokerID}/contracts/${contractID}/documents`, {
      headers: {
        "Content-Type": "application/json",
      },
      data: documentIDs,
    }),
  };
};

export const toggleBrokerUploadToast = (open: boolean) => {
  return {
    type: ACTION_TYPES.TOGGLE_UPLOAD_TOAST,
    meta: { open },
  };
};
